import './CurrentPlan.scss'
import { useCreateCustomerPortalSessionMutation, useCreateInvoicePreviewMutation, useGetBillingCustomerQuery, useGetPlansQuery } from '../../../store/plan/plan'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { Col, Container, Placeholder, Row } from 'react-bootstrap'
import React, { useEffect } from 'react'
import { Button } from '../../../components/Button/Button'
import { formatDateDDMMYYYY } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { ErrorResponse, NO_PAYMENT_METHOD_FOUND } from '../../../common/errorMessages'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import classNames from 'classnames'

export const CurrentPlan = () => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)

  const { data: plans } = useGetPlansQuery(undefined)
  const { data: customer, isFetching: customerLoading } = useGetBillingCustomerQuery({ accountId })
  const [openCustomerPortalSession] = useCreateCustomerPortalSessionMutation()
  const [createInvoicePreview, { error: previewError, data: createInvoicePreviewResponse }] = useCreateInvoicePreviewMutation()
  const { getQuotaValue } = useZocksQuota()
  const usedSessionsInPeriod = getQuotaValue('room.usedSessionsInPeriod.value')
  const usedAssistantSessionsInPeriod = getQuotaValue('room.usedAssistantSessionsInPeriod.value')
  const freeAssistantSeatMultiplier = Number(getQuotaValue('identity.sidecarSeats.value'))
  const hideBillingInfo = !!customer?.data.enterpriseSubscription?.enterpriseBillingConfig?.package.hideBillingInfo

  useEffect(() => {
    if (customer && customer.data.subscription && !hideBillingInfo) {
      createInvoicePreview({
        accountId,
      })
    }
  }, [accountId, createInvoicePreview, customer, hideBillingInfo])

  if (customerLoading || !customer || !plans || (customer.data.subscription && !createInvoicePreviewResponse && !previewError && !hideBillingInfo)) {
    return <Placeholder xs={12} size='lg' />
  }

  const customerPlan = plans.data.find((plan) => plan.id === customer.data.subscription?.planId)

  const isPlanChanging =
    customer.data.subscription &&
    customer.data.upcomingSubscription &&
    (customer.data.subscription.planId !== customer.data.upcomingSubscription.planId ||
      customer.data.subscription.period !== customer.data.upcomingSubscription.period ||
      customer.data.subscription.numberOfSeats > customer.data.upcomingSubscription.numberOfSeats ||
      (customer.data.subscription.additionalProducts['assistant']?.quantity ?? 0) >
        (customer.data.upcomingSubscription.additionalProducts['assistant']?.quantity ?? 0))

  const upcomingPlan = plans.data.find((plan) => customer.data.upcomingSubscription && plan.id === customer.data.upcomingSubscription.planId)

  const handleBillingHistoryClick = tracked(
    async () => {
      const response = await openCustomerPortalSession({
        accountId,
      }).unwrap()
      if (response.data.url) {
        window.location.href = response.data.url
      }
    },
    { eventName: TrackEvent.PLAN_BILLING_HISTORY_OPENED },
  )

  const getUpcomingInvoiceBody = () => {
    if (!customer.data.subscription || hideBillingInfo) {
      return null
    }

    if (createInvoicePreviewResponse) {
      return (
        <>
          Next payment: {formatDateDDMMYYYY(customer.data.subscription.currentPeriodEnd)}. You will be charged for{' '}
          <b>${createInvoicePreviewResponse.data.amountDue / 100}</b>.
        </>
      )
    }

    if (previewError && 'data' in previewError) {
      const errorMessages = (previewError.data as ErrorResponse).errors.map((error: any) => error.message)
      if (errorMessages.some((errorMessage) => errorMessage.match(NO_PAYMENT_METHOD_FOUND))) {
        if (!customer.data.freeTrial || customer.data.freeTrial.endedAt) {
          return null
        }
        return <>Your subscription will be cancelled at {formatDateDDMMYYYY(customer.data.subscription.currentPeriodEnd)}.</>
      }
    }

    return null
  }

  if (!customer.data.subscription || !customerPlan || (!createInvoicePreviewResponse && !previewError)) {
    return null
  }

  const assistantSeats =
    (customer.data.upcomingSubscription?.additionalProducts['assistant']?.quantity ?? 0) +
    freeAssistantSeatMultiplier * (customer.data.upcomingSubscription?.numberOfSeats ?? 0)

  return (
    <Container className='p-0 zocks-mb-4'>
      <Row className='mb-3'>
        <Col>
          <div className='lead-medium'>Your Current Plan</div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div
            className={classNames('p-4 zocks-border-0.5 mb-3', {
              'zocks-bg-color-light-blue': customer.data.subscription,
              'zocks-bg-color-warning': !customer.data.subscription,
            })}
          >
            <Container>
              <Row>
                <Col>
                  <h5>
                    <div className='d-flex gap-2'>
                      <i className='bi bi-calendar2-event' />
                      {customerPlan.name}
                    </div>
                  </h5>
                </Col>
              </Row>
              {!!usedSessionsInPeriod || !!usedAssistantSessionsInPeriod ? (
                <Row>
                  <Col>
                    You have used{' '}
                    {!!usedSessionsInPeriod && (
                      <>
                        <b>{usedSessionsInPeriod}</b> meeting(s){' '}
                      </>
                    )}
                    {!!usedSessionsInPeriod && !!usedAssistantSessionsInPeriod && <>and </>}
                    {!!usedAssistantSessionsInPeriod && (
                      <>
                        <b>{usedAssistantSessionsInPeriod}</b> assistant meeting(s){' '}
                      </>
                    )}
                    in your current period.
                  </Col>
                </Row>
              ) : null}
              <Row className='d-flex justify-content-between gap-3'>
                <Col xs={12} md='auto'>
                  {customer.data.subscription.period === 'month' ? 'Monthly subscription package. ' : 'Annual subscription package. '}
                  {getUpcomingInvoiceBody()}
                </Col>
                {!hideBillingInfo ? (
                  <Col xs='12' md={'auto'}>
                    <Button variant='link' onClick={() => handleBillingHistoryClick()}>
                      Billing history
                    </Button>
                  </Col>
                ) : null}
              </Row>
              {customer.data.upcomingSubscription && upcomingPlan && isPlanChanging ? (
                <Row>
                  <Col className='change-plan-container lead-extra-small'>
                    <i className='bi bi-exclamation-circle-fill' /> Your plan will be changed in the end of your period to the following:
                    {upcomingPlan.name} {customer.data.upcomingSubscription.period === 'month' ? 'Monthly paid' : 'Annual paid'},{' '}
                    {customer.data.upcomingSubscription.numberOfSeats} seat{assistantSeats ? ` and ${assistantSeats} assistant seat` : ''}.
                  </Col>
                </Row>
              ) : null}
            </Container>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
